import { useState } from "react";
import { agoText } from "../chat/api";
import { isWebLink } from "../lib/chores";
import type { Chore, ChoreDraft } from "../lib/chores";
import { tx } from "../i18n";
import { Button } from "./Button";
import { Alert, Card, EmptyState } from "./Card";
import { Field, Input } from "./Field";
import { Icon } from "./Icon";
import "./Chores.css";

/* ══ 게시판(운영 할 일) 부품 — 시안 docs/screens/pr-port.md §22 ══
   폼은 공통 .b-field/.b-input/.b-textarea, 목록은 .b-dt + .b-check, 작성자는 .b-badge(아이콘 + 글자).
   한 줄 안 단추는 「수정」 하나다 — 지우기는 수정 폼 안에 둔다(AGENTS.md 「표 한 줄 안 단추는 하나까지」).
   끝낸 줄은 취소선 + ✓ 글자로 — 흐린 색만으로 가르지 않는다. */

const EMPTY: ChoreDraft = { title: "", body: "", link: "" };

/** 새 항목 · 고치기 폼. `initial` 이 있으면 고치기다 — 그때만 「삭제」가 선다(두 번 누른다). */
export function ChoreForm({
  initial,
  busy,
  error,
  onSubmit,
  onCancel,
  onDelete,
}: {
  initial?: ChoreDraft;
  busy?: boolean;
  error?: string | null;
  onSubmit: (d: ChoreDraft) => void;
  onCancel?: () => void;
  onDelete?: () => void;
}) {
  const [d, setD] = useState<ChoreDraft>(initial ?? EMPTY);
  const [armed, setArmed] = useState(false);
  const editing = Boolean(initial);
  const set = (k: keyof ChoreDraft) => (v: string) => setD((p) => ({ ...p, [k]: v }));
  return (
    <form
      className="chform"
      onSubmit={(e) => {
        e.preventDefault();
        if (d.title.trim()) onSubmit(d);
      }}
    >
      <Field label={tx("chores.form.title")}>
        {(fid) => <Input id={fid} value={d.title} onChange={(e) => set("title")(e.target.value)} required autoFocus={editing} placeholder={tx("chores.form.titlePh")} />}
      </Field>
      <Field label={tx("chores.form.body")} hint={tx("chores.form.optional")}>
        {(fid) => <textarea id={fid} className="b-textarea" rows={3} value={d.body} onChange={(e) => set("body")(e.target.value)} />}
      </Field>
      <Field label={tx("chores.form.link")} hint={tx("chores.form.linkHint")}>
        {(fid) => <Input id={fid} value={d.link} onChange={(e) => set("link")(e.target.value)} placeholder="https://… · docs/…" />}
      </Field>
      {error ? <Alert tone="error">{error}</Alert> : null}
      <div className="chform__acts">
        {editing && onDelete ? (
          <Button
            type="button"
            variant={armed ? "danger" : "ghost"}
            disabled={busy}
            onClick={() => (armed ? onDelete() : setArmed(true))}
          >
            {armed ? tx("chores.deleteConfirm") : tx("common.action.delete")}
          </Button>
        ) : null}
        <span className="chform__gap" />
        {onCancel ? (
          <Button type="button" onClick={onCancel} disabled={busy}>
            {tx("common.action.cancel")}
          </Button>
        ) : null}
        <Button type="submit" variant="primary" loading={busy} disabled={!d.title.trim()}>
          {editing ? tx("common.action.save") : tx("chores.form.create")}
        </Button>
      </div>
    </form>
  );
}

/** 작성자 배지 — 모양(아이콘)과 글자가 함께 간다. 이름은 신원의 기록 이름이다. */
export function AuthorBadge({ c }: { c: Pick<Chore, "authorKind" | "createdBy"> }) {
  const agent = c.authorKind === "agent";
  return (
    <span className={`b-badge${agent ? " b-badge-info" : ""} chrow__who`} title={c.createdBy}>
      <Icon name={agent ? "bot" : "user"} />
      {agent ? tx("chores.author.agent") : tx("chores.author.human")} · {c.createdBy}
    </span>
  );
}

/** 한 줄. 체크 = 끝냄/되돌림, 「수정」 = 그 자리에 폼을 편다. */
export function ChoreRow({
  c,
  busy,
  editing,
  editError,
  now,
  onToggle,
  onEdit,
  onSave,
  onCancel,
  onDelete,
}: {
  c: Chore;
  busy?: boolean;
  editing?: boolean;
  editError?: string | null;
  now?: number;
  onToggle: (done: boolean) => void;
  onEdit: () => void;
  onSave: (d: ChoreDraft) => void;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const done = Boolean(c.doneAt);
  if (editing) {
    return (
      <div className="b-dt-row chrow is-editing">
        <ChoreForm
          initial={{ title: c.title, body: c.body ?? "", link: c.link ?? "" }}
          busy={busy}
          error={editError}
          onSubmit={onSave}
          onCancel={onCancel}
          onDelete={onDelete}
        />
      </div>
    );
  }
  return (
    <div className={`b-dt-row chrow${done ? " is-done" : ""}`}>
      <label className="b-check chrow__check">
        <input
          type="checkbox"
          checked={done}
          disabled={busy}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={done ? tx("chores.undoneAria", { title: c.title }) : tx("chores.doneAria", { title: c.title })}
        />
      </label>
      <div className="b-dt-cell is-primary chrow__main">
        <p className="chrow__title">
          {done ? <span className="chrow__mark">✓ </span> : null}
          <span className="chrow__text">{c.title}</span>
        </p>
        {c.body ? <p className="chrow__body">{c.body}</p> : null}
        <p className="caption chrow__meta">
          <AuthorBadge c={c} />
          <span title={c.createdAt}>{agoText(c.createdAt, now)}</span>
          {done ? <span>{tx("chores.doneBy", { who: c.doneBy ?? tx("chat.time.unknown"), ago: agoText(c.doneAt, now) })}</span> : null}
          {c.link ? (
            <span className="chrow__link">
              {tx("chores.ref")}{" "}
              {isWebLink(c.link) ? (
                <a href={c.link} target="_blank" rel="noreferrer noopener">
                  {c.link}
                </a>
              ) : (
                <code>{c.link}</code>
              )}
            </span>
          ) : null}
        </p>
      </div>
      <div className="b-dt-cell chrow__acts">
        <Button size="sm" variant="ghost" onClick={onEdit} disabled={busy}>
          {tx("common.action.edit")}
        </Button>
      </div>
    </div>
  );
}

/** 목록 — 남은 일 판 하나 + 끝낸 일은 접는다(펼침 단추에 수). 남은 일이 0 이면 그렇다고 말한다. */
export function ChoreList({
  rows,
  showDone,
  onShowDone,
  row,
}: {
  rows: Chore[];
  showDone: boolean;
  onShowDone: (on: boolean) => void;
  row: (c: Chore) => React.ReactNode;
}) {
  const open = rows.filter((c) => !c.doneAt);
  const done = rows.filter((c) => c.doneAt);
  return (
    <div className="chlist">
      <Card title={tx("chores.openTitle", { n: open.length })}>
        {open.length ? <div className="b-dt chlist__dt">{open.map(row)}</div> : <EmptyState compact icon="check" title={tx("chores.openEmpty")} />}
      </Card>
      {done.length ? (
        <Card
          title={tx("chores.doneTitle", { n: done.length })}
          meta={
            <Button size="sm" variant="ghost" onClick={() => onShowDone(!showDone)} aria-expanded={showDone}>
              {showDone ? tx("common.action.collapse") : tx("common.action.expand")}
            </Button>
          }
        >
          {showDone ? <div className="b-dt chlist__dt">{done.map(row)}</div> : <p className="caption">{tx("chores.doneFolded")}</p>}
        </Card>
      ) : null}
    </div>
  );
}
